import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollProgress from "../components/ScrollProgress";
import BackToTop from "../components/BackToTop";
import { useLenis } from "../hooks/useLenis";
import { useReveal } from "../hooks/useReveal";
import useSEO from "../hooks/useSEO";
import { ArrowRight, Check, Package, FlaskConical, Target } from "lucide-react";

const PRODUCT_IMAGES = {
  "refined-sunflower-oil":
    "https://images.pexels.com/photos/5056853/pexels-photo-5056853.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1300",
  "crude-sunflower-oil":
    "https://images.unsplash.com/photo-1709293287084-544575bb1398?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxzdW5mbG93ZXIlMjBvaWwlMjBwb3VyaW5nfGVufDB8fHx8MTc3NzEzMjEyN3ww&ixlib=rb-4.1.0&q=85",
  "blended-vegetable-oils":
    "https://images.unsplash.com/photo-1532634766-c2761df5daba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtYW51ZmFjdHVyaW5nJTIwZmFjaWxpdHklMjBmYWN0b3J5JTIwY2xlYW58ZW58MHx8fHwxNzc3MTMyMTI3fDA&ixlib=rb-4.1.0&q=85",
  "meal-by-products":
    "https://www.saipol.com/uploads/2020/08/PM_CETIOM_240605_512.jpg",
};

const FALLBACK =
  "https://images.unsplash.com/photo-1574323347407-f5e1c0cf4b7e?w=1400&auto=format&fit=crop&q=80";

const ProductDetail = () => {
  const { slug } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const ref = useReveal();
  useLenis();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  const product = t.products.list.find((p) => p.slug === slug);

  useSEO({
    title: product
      ? `${product.name} — EKOBIOTEK Ukraine`
      : "Product — EKOBIOTEK Ukraine",
    description: product?.fullDesc || product?.desc,
    canonical: `https://ekobiotek-ukraine.com.ua/product/${slug}`,
    ogImage: PRODUCT_IMAGES[slug] || FALLBACK,
  });

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8 pt-32">
          <h1 className="text-3xl font-bold mb-3">Product not found</h1>
          <button
            onClick={() => navigate("/")}
            className="mt-4 px-6 py-3 bg-[#1A201C] text-white rounded-full text-sm tracking-wider uppercase"
          >
            Home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const image = PRODUCT_IMAGES[product.slug] || FALLBACK;

  return (
    <div ref={ref} data-testid="product-detail-page" className="relative bg-[#FDFBF7]">
      <ScrollProgress />
      <Header />

      {/* Hero banner */}
      <section className="relative h-[60vh] min-h-[420px] w-full overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-black/10" />

        <div className="relative z-10 h-full flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-14">
            <Link
              to="/"
              data-testid="product-back-link"
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#DE9E36] mb-6"
            >
              {t.products.backToProducts}
            </Link>
            <div className="flex items-center gap-3 mb-5">
              <span className="px-3 py-1 text-[10px] tracking-[0.22em] uppercase border border-white/40 backdrop-blur-sm bg-white/10 rounded-full text-white">
                {product.tag}
              </span>
              <span className="overline text-[#F1C779]">{t.products.overline}</span>
            </div>
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] max-w-4xl">
              {product.name}
            </h1>
            <p className="mt-5 text-white/75 text-base sm:text-lg max-w-2xl leading-relaxed">
              {product.desc}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left: description + applications */}
            <div className="lg:col-span-7 reveal">
              <div className="flex items-center gap-3 mb-5">
                <span className="divider-line" />
                <span className="overline text-[#DE9E36]">{t.products.overline}</span>
              </div>
              <p className="text-lg text-[#1A201C] leading-relaxed">{product.fullDesc}</p>

              <div className="mt-14">
                <div className="flex items-center gap-3 mb-6">
                  <Target size={18} className="text-[#DE9E36]" strokeWidth={1.5} />
                  <h2 className="text-2xl font-bold tracking-tight">
                    {t.products.applicationsTitle}
                  </h2>
                </div>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {product.applications.map((a, i) => (
                    <li
                      key={i}
                      data-testid={`product-application-${i}`}
                      className="flex items-start gap-3 p-4 border border-[#E5E0D8] bg-white hover:bg-[#F3EFE7] transition-colors"
                    >
                      <Check size={16} className="flex-shrink-0 mt-0.5 text-[#DE9E36]" />
                      <span className="text-sm text-[#1A201C]">{a}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-14">
                <div className="flex items-center gap-3 mb-6">
                  <Package size={18} className="text-[#DE9E36]" strokeWidth={1.5} />
                  <h2 className="text-2xl font-bold tracking-tight">
                    {t.products.packagingTitle}
                  </h2>
                </div>
                <div className="flex flex-wrap gap-3">
                  {product.packaging.map((p, i) => (
                    <span
                      key={i}
                      data-testid={`product-packaging-${i}`}
                      className="px-4 py-2 border border-[#E5E0D8] bg-white text-sm text-[#1A201C]"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: specs card */}
            <aside className="lg:col-span-5 reveal">
              <div className="sticky top-28 bg-[#1A201C] text-white p-8">
                <div className="flex items-center gap-3 mb-6">
                  <FlaskConical size={18} className="text-[#DE9E36]" strokeWidth={1.5} />
                  <h2 className="text-xl font-bold tracking-tight">
                    {t.products.specsTitle}
                  </h2>
                </div>
                <div className="divide-y divide-white/10">
                  {product.specs.map((s, i) => (
                    <div
                      key={i}
                      data-testid={`product-spec-${i}`}
                      className="flex items-center justify-between py-3 text-sm"
                    >
                      <span className="text-white/60">{s.k}</span>
                      <span className="text-white font-medium">{s.v}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to={{ pathname: "/", hash: "#contact" }}
                  data-testid="product-quote-cta"
                  className="mt-8 group inline-flex items-center justify-between w-full gap-2 px-5 py-4 bg-[#DE9E36] text-[#1A201C] text-xs tracking-[0.22em] uppercase font-bold hover:bg-white transition-colors"
                >
                  {t.products.contactCta}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <div className="mt-5 text-xs text-white/50 leading-relaxed">
                  EKOBIOTEK-UKRAINA, LLC · Kherson, Ukraine
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default ProductDetail;
